import {getField, updateField} from 'vuex-map-fields';
import {
    ENROLMENT_TYPE,
    QUESTION_OPTION,
    QUESTION_TYPE,
    SURVEY_OPTION_DRAW_TYPE,
    SURVEY_OPTION_TYPE,
} from '@/common/consts/survey.js';
import {FILTER_TYPE} from '@/common/consts/filter.js';
import {getNowDate, toDateFromISOFormat, toISOFormat} from '@/common/utils/date.js';
import {REQUEST_STATE} from '@/iamteacherpc/consts/request.js';
import {getNextSequence} from '@/iamteacherpc/utils/sequence.js';
import {
    deleteImageActionFactory,
    mutations as imageMutations,
    uploadImageActionFactory,
} from '@/common/store/modules/common/editor/image.js';
import {
    actions as documentActions,
    mutations as documentMutations,
} from '@/common/store/modules/common/editor/document.js';
import {
    actions as questionListActions,
    mutations as questionListMutations,
} from '@/common/store/modules/common/editor/questionList.js';
import {convertToLatestEnrolmentPayload} from '@/common/store/modules/enrolment/payload.js';
import {ImageManagerForPayload, ImageManagerForRequestInfo} from '@/iamteacherpc/utils/image.js';
import i18n from '@common/i18n';

export const GET_FILTER_LABEL = 'GET_FILTER_LABEL';
export const GET_FILTER_INDEX = 'GET_FILTER_INDEX';
export const GET_FILTER_RANGE_VALUE = 'GET_FILTER_RANGE_VALUE';
export const GET_PAYLOAD = 'GET_PAYLOAD';
export const GET_REQUEST_INFO = 'GET_REQUEST_INFO';

export const INIT = 'INIT';
export const ADD_QUESTION = 'ADD_QUESTION';
export const DELETE_QUESTION = 'DELETE_QUESTION';
export const COPY_QUESTION = 'COPY_QUESTION';
export const DELETE_PROGRAM = 'DELETE_PROGRAM';
export const ADD_PROGRAM = 'ADD_PROGRAM';
export const SET_PROGRAMS = 'SET_PROGRAMS';
export const SET_QUESTION_DRAW_PRIORS = 'SET_QUESTION_DRAW_PRIORS';
export const SET_PROGRAM_DRAW_PRIORS = 'SET_PROGRAM_DRAW_PRIORS';
export const SET_ALLOW_DUPLICATE_APPLY = 'SET_ALLOW_DUPLICATE_APPLY';
export const ADD_PROGRAM_TIME = 'ADD_PROGRAM_TIME';
export const REMOVE_PROGRAM_TIME = 'REMOVE_PROGRAM_TIME';
export const ADD_PROGRAM_COST = 'ADD_PROGRAM_COST';
export const REMOVE_PROGRAM_COST = 'REMOVE_PROGRAM_COST';
export const SET_FORM_DATA = 'SET_FORM_DATA';
export const SET_FORM_DATA_FROM_REQUEST_INFO = 'SET_FORM_DATA_FROM_REQUEST_INFO';
export const ADD_AGREEMENT = 'ADD_AGREEMENT';
export const DELETE_AGREEMENT = 'DELETE_AGREEMENT';

export const UPLOAD_IMAGE = 'UPLOAD_IMAGE';
export const DELETE_IMAGE = 'DELETE_IMAGE';
export const UPLOAD_QUESTION_IMAGE = 'UPLOAD_QUESTION_IMAGE';
export const DELETE_QUESTION_IMAGE = 'DELETE_QUESTION_IMAGE';

export const MAX_QUESTION = 50;
export const MAX_AGREEMENTS = 10;
export const MAX_PROGRAM = 99;
export const MAX_PROGRAM_TIME = 7;
export const MAX_PROGRAM_COST = 10;

let startGrade = 'F1';
let endGrade = 'F3';

const store = {
    namespaced: true,

    state() {
        return {
            /**
             * Form 데이터
             * @type {EnrolmentFormInfo}
             */
            form: getInitForm(),

            options: {
                stopListChange: false,
                filterOptions: {
                    type: FILTER_TYPE.RANGE,
                    filters: [
                        {
                            label: '필터1',
                            value: 'F1'
                        },
                        {
                            label: '필터2',
                            value: 'F2'
                        },
                        {
                            label: '필터3',
                            value: 'F3'
                        },
                    ]
                }
            }
        }
    },

    getters: {
        getField,

        [GET_FILTER_LABEL](state) {
            return function (value) {
                return state.options.filterOptions.filters.find(filter => filter.value === value).label;
            }
        },

        [GET_FILTER_INDEX](state) {
            return function (value) {
                return state.options.filterOptions.filters.findIndex(filter => filter.value === value);
            }
        },

        [GET_FILTER_RANGE_VALUE](state, getters) {
            return function (startValue, endValue) {
                const values = [];
                const startIndex = getters[GET_FILTER_INDEX](startValue);
                const endIndex = getters[GET_FILTER_INDEX](endValue);

                for (let i = startIndex; i <= endIndex; i++) {
                    values.push(state.options.filterOptions.filters[i].value);
                }

                return values;
            }
        },

        /**
         * @param state
         * @param {EnrolmentFormInfo} state.form
         * @param getters
         * @returns {EnrolmentPayload}
         */
        [GET_PAYLOAD](state, getters) {
            const imageManager = new ImageManagerForPayload();

            const enrolmentQuestions = state.form.questions.map((/** @type QuestionInfoForm */question, index) => ({
                questionNo: question.questionNo,
                questionNumber: index + 1,
                question: question.question,
                questionType: QUESTION_TYPE.ENROLMENT,
                image: imageManager.setOrGetImage(question.image),
                maxChoice: question.maxChoice,
                enrolmentType: state.form.enrolmentType,

                programs: question.programs.map((/** @type ProgramInfoForm */program, index) => ({
                    programNo: program.programNo,
                    programNumber: index + 1,
                    programName: program.programName,
                    programDesc: program.programDesc,
                    limit: state.form.enrolmentType !== ENROLMENT_TYPE.RANDOM ? program.limit || 0 : undefined,
                    reservedLimit: state.form.enrolmentType !== ENROLMENT_TYPE.RANDOM ? program.reservedLimit || 0 : undefined,
                    filters: program.filters.map(filter => filter),
                    times: program.times.map(time => ({
                        day: time.day,
                        startHour: time.startHour,
                        startMinute: time.startMinute,
                        endHour: time.endHour,
                        endMinute: time.endMinute,
                    })),
                    costs: program.costs.map(cost => ({
                        name: cost.name,
                        value: cost.value,
                    })),
                    drawPriors: JSON.parse(JSON.stringify(program.drawPriors)),
                })),
            }));
            const agreements = !state.form.isUseAgreements ? [] : state.form.agreements.map((/** @type AgreementInfoForm */agreement, index) =>
                ({
                    questionNo: agreement.questionNo,
                    questionNumber: index + 1,
                    question: agreement.question,
                    questionDesc: agreement.questionDesc,
                    questionType: QUESTION_TYPE.ENROLMENT_AGREEMENT,
                    mandatory: agreement.mandatory,
                }),
            );

            const questions = [...enrolmentQuestions, ...agreements].map((question, index) => ({
                ...question,
                questionNumber: '' + (index + 1),
            }));

            return {
                version: 4,
                type: 'ENROLMENT',
                images: state.form.images.map(image => imageManager.setOrGetImage(image)),
                filterInfo: state.options.filterOptions,

                questions,
            }
        },

        /**
         * @param state
         * @param {EnrolmentFormInfo} state.form
         * @param getters
         * @returns {EnrolmentRequestInfo}
         */
        [GET_REQUEST_INFO](state, getters) {
            const imageManager = new ImageManagerForRequestInfo();

            function getDocuments({documents}) {
                if (!documents) {
                    return [];
                }
                return documents.filter(document => document.uploadState === REQUEST_STATE.SUCCESS)
                    .map(document => {
                        return {
                            type: 'DOCUMENT',
                            fileName: document.fileName,
                            documentNo: document.documentNo,
                            description: null,
                        };
                    });
            }

            const enrolmentQuestions = state.form.questions.flatMap((/** @type QuestionInfoForm */question, index) => [
                {
                    questionNo: question.questionNo,
                    parentQuestionNo: null,
                    questionNumber: '' + (index + 1),
                    question: question.question,
                    questionDesc: '',
                    questionType: QUESTION_TYPE.ENROLMENT,
                    questionOptions: [
                        {
                            optionType: QUESTION_OPTION.ENROLMENT_TYPE,
                            optionValue: state.form.enrolmentType,
                        },
                        {
                            optionType: QUESTION_OPTION.MAX_CHOICE,
                            optionValue: question.maxChoice,
                        },
                    ],
                },
                ...question.programs.map((/** @type ProgramInfoForm */program, index) => ({
                    questionNo: program.programNo,
                    parentQuestionNo: question.questionNo,
                    questionNumber: '' + (index + 1),
                    question: program.programName,
                    questionDesc: program.programDesc,
                    questionType: QUESTION_TYPE.ENROLMENT_PROGRAM,
                    questionOptions: [
                        ...program.filters.map(value => ({
                            optionType: QUESTION_OPTION.FILTER_TAG,
                            optionValue: value,
                        })),
                        ...(state.form.enrolmentType === ENROLMENT_TYPE.RANDOM ? [] : [
                            {
                                optionType: QUESTION_OPTION.LIMIT,
                                optionValue: program.limit || 0,
                            },
                            {
                                optionType: QUESTION_OPTION.RESERVED_LIMIT,
                                optionValue: program.reservedLimit || 0,
                            },
                        ]),
                        {
                            optionType: QUESTION_OPTION.ENROLMENT_TYPE,
                            optionValue: state.form.enrolmentType,
                        },
                    ],
                    drawPriors: program.drawPriors,
                    schedules: program.times.map(time => ({
                        dayOfWeek: time.day,
                        startTime: getIntTime({hour: time.startHour, minute: time.startMinute}),
                        endTime: getIntTime({hour: time.endHour, minute: time.endMinute}),
                    })),
                })),
            ]);
            const agreements = !state.form.isUseAgreements ? [] : state.form.agreements.map((/** @type AgreementInfoForm */agreement, index) =>
                ({
                    questionNo: agreement.questionNo,
                    questionNumber: '' + (index + 1),
                    question: agreement.question,
                    questionDesc: agreement.questionDesc,
                    questionType: QUESTION_TYPE.ENROLMENT_AGREEMENT,
                    questionOptions: [
                        {
                            optionType: QUESTION_OPTION.MANDATORY,
                            optionValue: (agreement.mandatory ? 1 : 0),
                        },
                    ],
                }),
            );

            const questions = [...enrolmentQuestions, ...agreements].map((question, index) => ({
                ...question,
                questionNumber: '' + (index + 1),
            }));

            return {
                title: state.form.title,
                surveyDesc: state.form.surveyDesc,
                enrolmentType: state.form.enrolmentType,
                isUseStartDate: state.form.isUseStartDate,
                startDate: state.form.isUseStartDate ? toISOFormat(state.form.startDate) : '1990-03-01T00:00:00+09:00',
                endDate: toISOFormat(state.form.endDate),
                draw: state.form.enrolmentType === ENROLMENT_TYPE.DRAW_PRIOR ? {
                    drawDateTime: state.form.isUseDrawingDate ? toISOFormat(state.form.drawingDate) : toISOFormat(state.form.endDate),
                } : null,

                attachments: imageManager.getAttachments(state.form),
                documents: getDocuments({documents: state.form.documents}),
                contents: JSON.stringify(getters[GET_PAYLOAD]),

                surveyOptionList: state.form.enrolmentType === ENROLMENT_TYPE.DRAW_PRIOR ? [
                    {
                        optionType: SURVEY_OPTION_TYPE.DRAW_TYPE,
                        optionValue: SURVEY_OPTION_DRAW_TYPE.DRAW_PRIOR,
                    },
                    {
                        optionType: SURVEY_OPTION_TYPE.ALLOW_APPLY_DUPLICATE,
                        optionValue: state.form.isAllowDuplicateApply,
                    },
                ] : [
                    {
                        optionType: SURVEY_OPTION_TYPE.ALLOW_APPLY_DUPLICATE,
                        optionValue: state.form.isAllowDuplicateApply,
                    },
                ],
                questions,
            };
        }
    },

    mutations: {
        updateField,

        ...imageMutations,
        ...documentMutations,
        ...questionListMutations,

        init(state, {filterOptions}) {
            startGrade = filterOptions.filters[0].value;
            endGrade = filterOptions.filters[filterOptions.filters.length - 1].value;

            state.options.filterOptions = filterOptions;
            state.form = getInitForm();
        },

        addQuestion(state) {
            if (state.form.questions.length >= MAX_QUESTION) {
                return;
            }

            state.form.questions = [...state.form.questions, createInitQuestion()];
        },

        deleteQuestion(state, index) {
            if (state.form.questions.length < 2) {
                throw new Error('You cant delete a last question');
            }

            state.form.questions = state.form.questions.filter((_, i) => i !== index);
        },

        copyQuestion(state, index) {
            if (state.form.questions.length === MAX_QUESTION) {
                return;
            }

            const newQuestion = JSON.parse(JSON.stringify(state.form.questions[index]));
            state.form.questions = [...state.form.questions, {
                ...newQuestion,
                questionNo: getNextSequence(),
                image: newQuestion.image && newQuestion.image.uploadState === REQUEST_STATE.SUCCESS ? newQuestion.image : null,
                programs: newQuestion.programs.map(program => ({
                    ...program,
                    programNo: getNextSequence()
                }))
            }];
        },

        deleteProgram(state, {questionIndex, programIndex}) {
            const question = state.form.questions[questionIndex];
            question.programs = question.programs.filter((_, i) => i !== programIndex);
        },

        addProgram(state, {questionIndex}) {
            const question = state.form.questions[questionIndex];

            if (question.programs.length >= MAX_PROGRAM) {
                return;
            }

            question.programs = [...question.programs, createInitProgram()];
        },

        setPrograms(state, {questionIndex, programs}) {
            state.form.questions[questionIndex].programs = programs.map(program => createProgram(
                program.programName,
                program.programDesc,
                program.filters,
                program.limit,
                program.reservedLimit,
                program.times,
                program.costs,
            ));
        },

        setQuestionDrawPriors(state, {questionIndex, drawPriors}) {
            const programs = state.form.questions[questionIndex].programs;
            programs.forEach(program => program.drawPriors = []);

            drawPriors.forEach(drawPrior => {
                const program = programs.find(program => program.programNo === drawPrior.programNo);
                if (!program) {
                    return;
                }

                program.drawPriors.push(drawPrior.data);
            });
        },

        setProgramDrawPriors(state, {program, drawPriors}) {
            program.drawPriors = drawPriors;
        },

        setAllowDuplicateApply(state, {isAllowDuplicateApply}) {
            state.form.isAllowDuplicateApply = isAllowDuplicateApply;
        },

        addProgramTime(state, {program}) {
            if (program.times.length >= MAX_PROGRAM_TIME) {
                return;
            }

            program.times.push(createProgramTime());
        },

        removeProgramTime(state, {program, index}) {
            program.times = program.times.filter((time, timeIndex) => timeIndex !== index);
        },

        addProgramCost(state, {program}) {
            if (program.costs.length >= MAX_PROGRAM_COST) {
                return;
            }

            program.costs.push(createProgramCost());
        },

        removeProgramCost(state, {program, index}) {
            program.costs = program.costs.filter((time, costIndex) => costIndex !== index);
        },

        setFormData(state, newValue) {
            state.form = newValue;
        },

        /**
         * 등록용 데이터에서 폼 데이터를 적용
         * @param state
         * @param {SurveyRequestInfo} requestInfo
         */
        setFormDataFromRequestInfo(state, requestInfo) {
            /**
             * @type {SurveyInfo}
             */
            const payload = convertToLatestEnrolmentPayload(JSON.parse(requestInfo.contents));

            if (!requestInfo.startDate) {
                requestInfo.startDate = '1990-03-01T00:00:00+09:00';
            }

            if (!requestInfo.endDate) {
                requestInfo.endDate = toISOFormat(getInitEndDate());
            }

            /**
             * @type {EnrolmentFormInfo}
             */
            const form = {
                title: requestInfo.title,
                surveyDesc: requestInfo.surveyDesc,
                images: payload.images.map(image => {
                    return {
                        ...image,
                        uploadState: REQUEST_STATE.SUCCESS,
                    };
                }) || [],
                documents: requestInfo.documents.map(document => {
                    return {
                        documentNo: document.documentNo,
                        fileName: document.fileName,
                        fileSize: document.fileSize,
                        uploadState: REQUEST_STATE.SUCCESS,
                    };
                }) || [],
                isUseStartDate: requestInfo.startDate.split('-')[0] !== '1990',
                isUseDrawingDate: requestInfo.draw ? requestInfo.draw.drawDateTime !== requestInfo.endDate : false,
                startDate: requestInfo.startDate.split('-')[0] !== '1990' ? toDateFromISOFormat(requestInfo.startDate) : getInitStartDate(),
                endDate: toDateFromISOFormat(requestInfo.endDate),
                drawingDate: requestInfo.draw ? toDateFromISOFormat(requestInfo.draw.drawDateTime) : toDateFromISOFormat(requestInfo.endDate),
                enrolmentType: payload.questions[0].enrolmentType,
                isAllowDuplicateApply: requestInfo.surveyOptions.find(option => option.optionType === SURVEY_OPTION_TYPE.ALLOW_APPLY_DUPLICATE)?.optionValue !== 'false',

                questions: payload.questions.filter(question => !question.questionType || question.questionType === QUESTION_TYPE.ENROLMENT || question.questionType === QUESTION_TYPE.ENROLMENT_QUESTION)
                                  .map(question => ({
                    questionNo: question.questionNo,
                    question: question.question,
                    image: question.image && {
                        ...question.image,
                        uploadState: REQUEST_STATE.SUCCESS,
                    } || null,
                    maxChoice: question.maxChoice,

                    programs: question.programs.map(program => ({
                        programNo: program.programNo,
                        programName: program.programName,
                        programDesc: program.programDesc,
                        limit: program.limit,
                        reservedLimit: program.reservedLimit,
                        filters: program.filters,
                        /**
                         * 2024.03.13
                         * 임시 저장시 시간 없음, 미설정 구분하기 위해서 기본으로 생성하는 createProgramTime() 제거함
                         */
                        // times: program.times.length ? program.times : [createProgramTime()],
                        times: program.times.length ? program.times : [],
                        costs: program.costs,
                        drawPriors: program.drawPriors,
                    }))
                                  })),

                isUseAgreements: payload.questions.filter(question => question.questionType === QUESTION_TYPE.ENROLMENT_AGREEMENT).length > 0,
                agreements: (() => {
                    const agreements = payload.questions.filter(question => question.questionType === QUESTION_TYPE.ENROLMENT_AGREEMENT);
                    if (agreements.length === 0) {
                        return [createInitAgreement()];
                    }
                    return agreements;
                })(),
            };

            state.form = form;
        },

        addAgreement(state) {
            if (state.form.agreements.length >= MAX_AGREEMENTS) {
                return;
            }

            state.form.agreements = [...state.form.agreements, createInitAgreement()];
        },
        deleteAgreement(state, {agreementIndex}) {
            state.form.agreements = state.form.agreements.filter((_, i) => i !== agreementIndex);
        },
    },

    actions: {
        ...documentActions,
        ...questionListActions,

        [UPLOAD_IMAGE]: uploadImageActionFactory({path: 'form'}),
        [UPLOAD_QUESTION_IMAGE]: uploadImageActionFactory({objName: 'question'}),

        [DELETE_IMAGE]: deleteImageActionFactory({path: 'form'}),
        [DELETE_QUESTION_IMAGE]: deleteImageActionFactory({objName: 'question'}),

        [INIT]({commit}, {filterOptions}) {
            commit('init', {filterOptions});
        },

        [ADD_QUESTION]({commit}) {
            commit('addQuestion');
        },

        [DELETE_QUESTION]({commit}, index) {
            commit('deleteQuestion', index);
        },

        [COPY_QUESTION]({commit}, index) {
            commit('copyQuestion', index);
        },

        [DELETE_PROGRAM]({commit}, {questionIndex, programIndex}) {
            commit('deleteProgram', {questionIndex, programIndex});
        },

        [ADD_PROGRAM]({commit}, {questionIndex}) {
            commit('addProgram', {questionIndex});
        },

        [SET_PROGRAMS]({commit}, {questionIndex, programs}) {
            commit('setPrograms', {questionIndex, programs});
        },

        [SET_QUESTION_DRAW_PRIORS]({commit}, {questionIndex, drawPriors}) {
            commit('setQuestionDrawPriors', {questionIndex, drawPriors});
        },

        [SET_PROGRAM_DRAW_PRIORS]({commit}, {program, drawPriors}) {
            commit('setProgramDrawPriors', {program, drawPriors});
        },

        [SET_ALLOW_DUPLICATE_APPLY]({commit}, {isAllowDuplicateApply}) {
            commit('setAllowDuplicateApply', {isAllowDuplicateApply});
        },

        [ADD_PROGRAM_TIME]({commit}, {program}) {
            commit('addProgramTime', {program});
        },

        [REMOVE_PROGRAM_TIME]({commit}, {program, index}) {
            commit('removeProgramTime', {program, index});
        },

        [ADD_PROGRAM_COST]({commit}, {program}) {
            commit('addProgramCost', {program});
        },

        [REMOVE_PROGRAM_COST]({commit}, {program, index}) {
            commit('removeProgramCost', {program, index});
        },

        [SET_FORM_DATA]({commit}, newValue) {
            commit('setFormData', newValue);
        },

        [SET_FORM_DATA_FROM_REQUEST_INFO]({commit}, requestInfo) {
            commit('setFormDataFromRequestInfo', requestInfo);
        },

        [ADD_AGREEMENT]({commit}) {
            commit('addAgreement');
        },

        [DELETE_AGREEMENT]({commit}, {agreementIndex}) {
            commit('deleteAgreement', {agreementIndex});
        },
    }
};

/**
 * 수강신청 폼 초기 데이터를 반환한다.
 * @returns {EnrolmentFormInfo}
 */
export function getInitForm() {
    return {
        title: '',
        surveyDesc: '',

        images: [],
        documents: [],

        isUseStartDate: false,
        isUseDrawingDate: false,
        startDate: getInitStartDate(),
        endDate: getInitEndDate(),
        drawingDate: getInitEndDate(),
        enrolmentType: ENROLMENT_TYPE.RANDOM,
        isAllowDuplicateApply: true,

        isUseAgreements: false,
        questions: [
            createInitQuestion(),
        ],
        agreements: [
            createInitAgreement(),
        ],
    }
}

/**
 * form에서 사용할 질문 초기 정보를 반환한다.
 * @returns {QuestionInfoForm}
 */
function createInitQuestion() {
    return createQuestion('', null, 0, [
        createInitProgram(),
        createInitProgram(),
        createInitProgram(),
    ]);
}

function createInitAgreement() {
    return createAgreement(
        '개인정보 수집 및 이용 동의',
        '1. 수집 항목: 수집 항목을 입력해 주세요.\n' +
        '2. 수집 목적: 수집 목적을 입력해 주세요.\n' +
        '3. 보유 기간: 보유 기간을 입력해 주세요.\n' +
        '※ 개인정보보호법에 의거하여 개인정보 수집 및 이용에 따른 동의를 거부할 수 있으나, 동의를 거부할 경우 신청에 제한을 받을 수 있습니다.', true);
}

function createAgreement(question, questionDesc, mandatory) {
    return {
        questionNo: getNextSequence(),
        question,
        questionDesc,
        mandatory,
    };
}

/**
 * form에서 사용할 프로그램 초기 정보를 반환한다.
 * @returns {ProgramInfoForm}
 */
function createInitProgram() {
    return createProgram('');
}

/**
 * form에서 사용한 프로그램 정보를 생성한다.
 * @param programName
 * @param [filters]
 * @param [limit]
 * @param [reservedLimit]
 * @param [times]
 * @param [costs]
 * @returns {ProgramInfoForm}
 */
function createProgram(programName,programDesc, filters = [], limit, reservedLimit, times, costs) {
    if (!times) {
        times = [createProgramTime()];
    }

    if (!costs) {
        costs = [
            createProgramCost({name: i18n.t('EnrolmentQuestionProgramCostEditor_cost1')}),
            createProgramCost({name: i18n.t('EnrolmentQuestionProgramCostEditor_cost2')}),
            createProgramCost({name: i18n.t('EnrolmentQuestionProgramCostEditor_cost3')}),
        ];
    }

    return {
        programNo: getNextSequence(),
        programName,
        programDesc,
        filters,
        limit,
        reservedLimit,
        times,
        costs,
        drawPriors: [],
    }
}

function createProgramTime() {
    return {
        day: null,
        startHour: null,
        startMinute: null,
        endHour: null,
        endMinute: null,
    };
}

function createProgramCost({name, value} = {}) {
    return {
        name,
        value,
    }
}

/**
 * form에서 사용할 질문 정보를 생성한다.
 * @param {string} question
 * @param {?ImageInfo} image
 * @param {number} maxChoice
 * @param {ProgramInfoForm[]} programs
 * @returns {QuestionInfoForm}
 */
function createQuestion(question, image, maxChoice, programs) {
    return {
        questionNo: getNextSequence(),
        question,
        image,
        maxChoice,
        programs
    }
}

/**
 * 신청 시작일 초기 데이터를 반환한다.
 * @returns {Date}
 */
function getInitStartDate() {
    const date = getNowDate();
    date.setDate(date.getDate() + 1);
    date.setHours(18);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
}

/**
 * 신청 종료일 초기 데이터를 반환한다.
 * @returns {Date}
 */
function getInitEndDate() {
    const date = getNowDate();
    date.setDate(date.getDate() + 2);
    date.setHours(18);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
}

/**
 * 시간/분 텍스트로부터 시간 값 계산
 * @param hour
 * @param minute
 * @returns {number}
 */
export function getIntTime({hour, minute}) {
    return (parseInt(hour || '0', 10) * 100) + parseInt(minute || '0', 10);
}

export default store;
